import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';

// FAQ Data for Vitrier Villiers-sur-Marne
export const faqVitrerieVilliers = [
  {
    question: "Quel est le délai d'intervention pour un vitrier à Villiers-sur-Marne ?",
    answer: "Notre vitrier à Villiers-sur-Marne intervient en moins d'1 heure en cas d'urgence. Disponible 24h/24 et 7j/7, nous assurons un dépannage vitrerie rapide pour toute vitre cassée ou bris de glace dans le Val-de-Marne (94)."
  },
  {
    question: "Combien coûte un remplacement de vitre à Villiers-sur-Marne ?",
    answer: "Le prix d'un remplacement de vitre dépend du type de vitrage (simple, double vitrage, triple vitrage) et des dimensions. Nous proposons un devis gratuit et transparent avant toute intervention. Comptez en moyenne entre 80€ et 300€ selon le type de vitre."
  },
  {
    question: "Intervenez-vous en urgence pour un bris de glace ?",
    answer: "Oui, NGL SERVICES intervient en urgence 24h/24 pour tout bris de glace à Villiers-sur-Marne et dans le Val-de-Marne. Nous sécurisons immédiatement les lieux et procédons au remplacement de la vitre cassée dans les meilleurs délais."
  },
  {
    question: "Quelles sont les villes couvertes autour de Villiers-sur-Marne ?",
    answer: "Notre vitrier intervient à Villiers-sur-Marne (94350) et dans toutes les communes voisines : Champigny-sur-Marne, Bry-sur-Marne, Nogent-sur-Marne, Le Perreux-sur-Marne, Saint-Maur-des-Fossés, Créteil, Noisy-le-Grand, Neuilly-sur-Marne et toute l'Île-de-France."
  },
  {
    question: "Installez-vous du double vitrage en rénovation ?",
    answer: "Oui, notre vitrier miroitier est spécialisé dans l'installation de double vitrage en rénovation à Villiers-sur-Marne. Nous réalisons la dépose de vos anciens vitrages et l'installation de doubles vitrages isolants pour améliorer votre isolation thermique et phonique."
  },
  {
    question: "Réparez-vous les baies vitrées à Villiers-sur-Marne ?",
    answer: "Oui, nous intervenons pour la réparation et le remplacement de baies vitrées à Villiers-sur-Marne. Notre vitrier se déplace pour tout type de baie vitrée : coulissante, à galandage, fixe. Devis gratuit sur place."
  },
  {
    question: "Proposez-vous des vitrages de sécurité anti-effraction ?",
    answer: "Oui, NGL SERVICES installe des vitrages de sécurité anti-effraction à Villiers-sur-Marne : verre feuilleté, verre trempé, vitrage retardateur d'effraction. Idéal après un cambriolage ou pour renforcer la sécurité de votre habitation ou commerce."
  },
  {
    question: "Intervenez-vous pour les vitrines de commerce ?",
    answer: "Oui, notre vitrier intervient pour les professionnels à Villiers-sur-Marne : remplacement de vitrine commerce, sécurisation après vandalisme, pose de vitrine magasin. Intervention rapide pour minimiser la fermeture de votre établissement."
  }
];

// Generate FAQ Schema.org JSON-LD
export const generateFAQSchema = (faqs) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

// FAQ Section Component
export const FAQSection = ({ 
  faqs = faqVitrerieVilliers, 
  title = "Questions fréquentes - Vitrier Villiers-sur-Marne",
  subtitle = "Trouvez les réponses à vos questions sur nos services de vitrerie"
}) => {
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-[hsl(215,25%,15%)] mb-4 text-center">
            {title}
          </h2>
          <p className="text-muted-foreground text-center mb-10">
            {subtitle}
          </p>
          
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`faq-${index}`}
                className="border border-gray-200 rounded-lg px-6 bg-[hsl(210,20%,98%)]"
              >
                <AccordionTrigger className="text-left font-semibold text-[hsl(215,25%,15%)] hover:no-underline py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
      
      {/* JSON-LD Schema for FAQ */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema(faqs))
        }}
      />
    </section>
  );
};

// Internal Links Section Component - Maillage interne renforcé
export const InternalLinksSection = ({ currentPage = '' }) => {
  const seoPages = [
    { path: '/vitrier-villiers-sur-marne', label: 'Vitrier Villiers-sur-Marne' },
    { path: '/vitrier-urgence-villiers-sur-marne', label: 'Vitrier urgence 24h/24' },
    { path: '/bris-de-glace-villiers-sur-marne', label: 'Bris de glace' },
    { path: '/remplacement-vitre-cassee-villiers-sur-marne', label: 'Vitre cassée' },
  ];

  const servicePages = [
    { path: '/services/vitrerie', label: 'Vitrerie & Miroiterie' },
    { path: '/services/fenetres', label: 'Fenêtres PVC/Alu' },
    { path: '/services/serrurerie', label: 'Serrurerie' },
    { path: '/services/volets', label: 'Volets roulants' },
  ];

  const cityPages = [
    { path: '/villes/villiers-sur-marne', label: 'Villiers-sur-Marne (94350)' },
    { path: '/villes/champigny-sur-marne', label: 'Champigny-sur-Marne' },
    { path: '/villes/bry-sur-marne', label: 'Bry-sur-Marne' },
    { path: '/villes/nogent-sur-marne', label: 'Nogent-sur-Marne' },
    { path: '/villes/saint-maur-des-fosses', label: 'Saint-Maur-des-Fossés' },
    { path: '/villes/creteil', label: 'Créteil' },
    { path: '/villes/noisy-le-grand', label: 'Noisy-le-Grand' },
    { path: '/villes/le-perreux-sur-marne', label: 'Le Perreux-sur-Marne' },
  ];

  return (
    <section className="py-12 bg-[hsl(210,20%,98%)] border-t">
      <div className="container mx-auto px-4">
        <h3 className="text-xl font-bold text-[hsl(215,25%,15%)] mb-8">
          Découvrir nos services de vitrerie en Île-de-France
        </h3>
        
        <div className="grid md:grid-cols-3 gap-8">
          {/* SEO Pages */}
          <div>
            <h4 className="font-semibold text-[hsl(207,78%,42%)] mb-4">Pages vitrerie</h4>
            <ul className="space-y-2">
              {seoPages.filter(p => p.path !== currentPage).map((page, i) => (
                <li key={i}>
                  <Link 
                    to={page.path} 
                    className="text-muted-foreground hover:text-[hsl(207,78%,42%)] hover:underline text-sm"
                  >
                    {page.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-[hsl(207,78%,42%)] mb-4">Nos services</h4>
            <ul className="space-y-2">
              {servicePages.map((page, i) => (
                <li key={i}>
                  <Link 
                    to={page.path} 
                    className="text-muted-foreground hover:text-[hsl(207,78%,42%)] hover:underline text-sm"
                  >
                    {page.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Cities */}
          <div>
            <h4 className="font-semibold text-[hsl(207,78%,42%)] mb-4">Zones d'intervention</h4>
            <ul className="space-y-2">
              {cityPages.map((page, i) => (
                <li key={i}>
                  <Link 
                    to={page.path} 
                    className="text-muted-foreground hover:text-[hsl(207,78%,42%)] hover:underline text-sm"
                  >
                    {page.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Additional SEO text */}
        <div className="mt-10 pt-8 border-t border-gray-200">
          <p className="text-sm text-muted-foreground">
            <strong>NGL SERVICES</strong> - Vitrier miroitier à Villiers-sur-Marne (94350). 
            Intervention rapide pour remplacement de vitre, bris de glace, double vitrage, baie vitrée. 
            Dépannage vitrerie urgence 24h/24 dans tout le Val-de-Marne : Champigny-sur-Marne, 
            Bry-sur-Marne, Nogent-sur-Marne, Saint-Maur-des-Fossés, Créteil, Noisy-le-Grand. 
            Devis gratuit ☎ <a href="tel:0781559050" className="text-[hsl(207,78%,42%)]">07 81 55 90 50</a>
          </p>
        </div>
      </div>
    </section>
  );
};

// Rich SEO Content Block Component
export const SEOContentBlock = ({ 
  city = "Villiers-sur-Marne",
  postalCode = "94350"
}) => {
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto prose prose-lg">
          <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">
            Vitrier miroitier de confiance à {city} ({postalCode})
          </h2>
          
          <p className="text-muted-foreground mb-4">
            <strong>NGL SERVICES</strong> est votre <strong>vitrier à {city}</strong> depuis plusieurs années. 
            Notre équipe de <strong>vitrier miroitier</strong> qualifié intervient en urgence pour tout 
            <strong> remplacement de vitre</strong>, <strong>bris de glace</strong> et installation de 
            <strong> double vitrage</strong> dans tout le secteur de <strong>{city} {postalCode}</strong> 
            et les communes environnantes du <strong>Val-de-Marne</strong>.
          </p>
          
          <p className="text-muted-foreground mb-4">
            Notre <strong>savoir-faire</strong> reconnu en <strong>vitrerie à {city}</strong> nous permet 
            d'intervenir 24h/24 et 7j/7 pour répondre à toutes vos urgences. Que vous ayez des 
            <strong> vitres cassées</strong> suite à un cambriolage, un <strong>bris de glace</strong> accidentel, 
            ou que vous souhaitiez installer une <strong>baie vitrée</strong> ou améliorer l'isolation avec un 
            <strong> double vitrage</strong>, notre <strong>vitrier miroitier</strong> est là pour vous.
          </p>

          <h3 className="text-xl font-bold text-[hsl(215,25%,15%)] mt-8 mb-4">
            Dépannage vitrerie à {city} et Val-de-Marne
          </h3>
          
          <p className="text-muted-foreground mb-4">
            Notre entreprise de <strong>vitrerie à {city}</strong> propose un service complet de 
            <strong> dépannage vitrerie</strong> : <strong>remplacement de vitre</strong> simple ou 
            <strong> double vitrage</strong>, réparation <strong>bris de glace</strong>, pose de 
            <strong> baie vitrée</strong>, vitrage sécurité anti-effraction. Notre <strong>vitrier miroitier</strong> 
            intervient également à <strong>Champigny-sur-Marne</strong>, Bry-sur-Marne, Nogent-sur-Marne et 
            dans tout le <strong>Val-de-Marne</strong>.
          </p>

          <h3 className="text-xl font-bold text-[hsl(215,25%,15%)] mt-8 mb-4">
            Pourquoi choisir notre vitrerie à {city} ?
          </h3>
          
          <ul className="space-y-2 text-muted-foreground mb-6">
            <li><strong>Intervention rapide</strong> : Vitrier à {city} sur place en moins d'1 heure</li>
            <li><strong>Disponibilité 24h/24</strong> : Dépannage vitrerie disponible jour et nuit</li>
            <li><strong>Devis gratuit</strong> : Estimation transparente pour vitres cassées, double vitrage</li>
            <li><strong>Savoir-faire reconnu</strong> : Vitrier miroitier expérimenté Val-de-Marne</li>
            <li><strong>Tous types de vitrage</strong> : Simple, double vitrage, baie vitrée, verre sécurité</li>
            <li><strong>Île-de-France</strong> : Intervention {city} {postalCode} et communes voisines</li>
          </ul>

          <h3 className="text-xl font-bold text-[hsl(215,25%,15%)] mt-8 mb-4">
            Zones d'intervention vitrier - Val-de-Marne et Île-de-France
          </h3>
          
          <p className="text-muted-foreground">
            En plus de <strong>{city}</strong>, notre <strong>vitrier miroitier</strong> intervient dans 
            les communes voisines : <Link to="/villes/champigny-sur-marne" className="text-[hsl(207,78%,42%)] hover:underline">Champigny-sur-Marne</Link>, 
            <Link to="/villes/bry-sur-marne" className="text-[hsl(207,78%,42%)] hover:underline"> Bry-sur-Marne</Link>, 
            <Link to="/villes/nogent-sur-marne" className="text-[hsl(207,78%,42%)] hover:underline"> Nogent-sur-Marne</Link>, 
            <Link to="/villes/le-perreux-sur-marne" className="text-[hsl(207,78%,42%)] hover:underline"> Le Perreux-sur-Marne</Link>, 
            <Link to="/villes/saint-maur-des-fosses" className="text-[hsl(207,78%,42%)] hover:underline"> Saint-Maur-des-Fossés</Link>, 
            <Link to="/villes/creteil" className="text-[hsl(207,78%,42%)] hover:underline"> Créteil</Link>, 
            <Link to="/villes/noisy-le-grand" className="text-[hsl(207,78%,42%)] hover:underline"> Noisy-le-Grand</Link>, 
            Neuilly-sur-Marne, Neuilly-Plaisance, Le Plessis-Trévise, Chennevières-sur-Marne. 
            <strong> Dépannage vitrerie</strong> rapide dans tout le <strong>Val-de-Marne</strong> et l'<strong>Île-de-France</strong>.
          </p>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
